import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, Calendar, User, ChevronLeft, ChevronRight, Tag as TagIcon } from 'lucide-react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SectionTitle from '../ui/SectionTitle';
import SEO from '../seo/SEO';
import { getAllPosts, formatDate, type Post } from '../../lib/posts';
import { stagger, fadeInUp } from '../../lib/animations';

/** Card de post individual na listagem */
const BlogCard: React.FC<{ post: Post }> = ({ post }) => (
  <motion.article
    variants={fadeInUp}
    className="group flex flex-col overflow-hidden rounded-2xl bg-slate-900 border border-slate-800 hover:border-amber-400/40 transition-colors duration-300"
  >
    {/* Thumbnail */}
    {post.thumbnailUrl && (
      <div className="overflow-hidden aspect-video">
        <img
          src={post.thumbnailUrl}
          alt={post.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
    )}

    <div className="flex flex-col flex-1 p-6">
      {/* Tags */}
      {post.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {post.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-400/10 text-amber-400 border border-amber-400/20"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Título */}
      <h2 className="font-display text-lg font-bold text-white leading-snug mb-2 group-hover:text-amber-400 transition-colors duration-200 line-clamp-3">
        {post.title}
      </h2>

      {/* TLDR */}
      <p className="text-slate-400 text-sm leading-relaxed mb-5 flex-1 line-clamp-3">
        {post.tldr}
      </p>

      {/* Meta + CTA */}
      <div className="flex items-center justify-between text-xs text-slate-500 pt-4 border-t border-slate-800">
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
            {formatDate(post.date)}
          </span>
          <span className="flex items-center gap-1">
            <User className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
            {post.author}
          </span>
        </div>
        <Link
          to={`/blog/${post.slug}`}
          className="flex items-center gap-1 text-amber-400 hover:text-amber-300 font-semibold transition-colors shrink-0 ml-4"
        >
          Ler artigo
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
        </Link>
      </div>
    </div>
  </motion.article>
);

/** Componente da nuvem de tags */
const TAGS_INITIAL_LIMIT = 18;

type TagItem = {
  name: string;
  count: number;
};

const TagCloud: React.FC<{
  allTags: TagItem[];
  activeTag: string | null,
  onTagClick: (tag: string | null) => void
}> = ({ allTags, activeTag, onTagClick }) => {
  const [showAllTags, setShowAllTags] = useState(false);

  const visibleTags = useMemo(() => {
    if (showAllTags) return allTags;

    const base = allTags.slice(0, TAGS_INITIAL_LIMIT);
    if (!activeTag) return base;

    const hasActiveInBase = base.some((tag) => tag.name === activeTag);
    if (hasActiveInBase) return base;

    const activeTagItem = allTags.find((tag) => tag.name === activeTag);
    return activeTagItem ? [...base.slice(0, TAGS_INITIAL_LIMIT - 1), activeTagItem] : base;
  }, [activeTag, allTags, showAllTags]);

  const hiddenCount = Math.max(allTags.length - visibleTags.length, 0);

  return (
    <motion.div
      variants={fadeInUp}
      initial="hidden"
      animate="visible"
      className="mb-10 rounded-2xl border border-slate-800 bg-slate-900/40 p-4 sm:p-5"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-slate-300">Filtrar por tag</span>
        {allTags.length > TAGS_INITIAL_LIMIT && (
          <button
            onClick={() => setShowAllTags((prev) => !prev)}
            className="text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors"
          >
            {showAllTags ? 'Mostrar menos' : `Mostrar mais (${hiddenCount})`}
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => onTagClick(null)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            activeTag === null
              ? 'bg-amber-400 text-slate-950'
              : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
          }`}
        >
          Todos
        </button>

        {visibleTags.map((tag) => (
          <button
            key={tag.name}
            onClick={() => onTagClick(tag.name)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeTag === tag.name
                ? 'bg-amber-400 text-slate-950'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <TagIcon className="h-3 w-3 inline mr-1" strokeWidth={2} />
            {tag.name}
          </button>
        ))}
      </div>
    </motion.div>
  );
};

/** Componente de paginação */
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <motion.div
      variants={fadeInUp}
      initial="hidden"
      animate="visible"
      className="flex items-center justify-center gap-2 mt-12"
    >
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={2} />
        Anterior
      </button>

      <div className="flex items-center gap-1">
        {getVisiblePages().map((page, index) => (
          <React.Fragment key={index}>
            {page === '...' ? (
              <span className="px-3 py-2 text-slate-500">...</span>
            ) : (
              <button
                onClick={() => onPageChange(page as number)}
                className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                  currentPage === page
                    ? 'bg-amber-400 text-slate-950'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {page}
              </button>
            )}
          </React.Fragment>
        ))}
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="flex items-center gap-1 px-3 py-2 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Próximo
        <ChevronRight className="h-4 w-4" strokeWidth={2} />
      </button>
    </motion.div>
  );
};

/** Página de listagem de artigos do blog (/blog) */
const BlogList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const allPosts = getAllPosts();

  // Estado da página atual (começando em 1)
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const activeTag = searchParams.get('tag');

  // Extrair todas as tags únicas
  const allTags = useMemo<TagItem[]>(() => {
    const tagCountMap = new Map<string, number>();

    allPosts.forEach((post) => {
      post.tags.forEach((tag) => {
        const currentCount = tagCountMap.get(tag) ?? 0;
        tagCountMap.set(tag, currentCount + 1);
      });
    });

    return Array.from(tagCountMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'pt-BR'));
  }, [allPosts]);

  // Filtrar posts por tag se houver filtro ativo
  const filteredPosts = useMemo(() => {
    if (!activeTag) return allPosts;
    return allPosts.filter(post =>
      post.tags.some(tag => tag.toLowerCase() === activeTag.toLowerCase())
    );
  }, [allPosts, activeTag]);

  // Calcular paginação
  const postsPerPage = 10;
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), Math.max(totalPages, 1));
  const startIndex = (safeCurrentPage - 1) * postsPerPage;
  const endIndex = startIndex + postsPerPage;
  const paginatedPosts = filteredPosts.slice(startIndex, endIndex);

  // Handlers
  const handleTagClick = (tag: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (tag) {
      newParams.set('tag', tag);
    } else {
      newParams.delete('tag');
    }
    newParams.set('page', '1'); // Reset para primeira página ao mudar filtro
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    const boundedPage = Math.min(Math.max(page, 1), Math.max(totalPages, 1));
    newParams.set('page', boundedPage.toString());
    setSearchParams(newParams);
  };

  // Título dinâmico baseado no filtro
  const getPageTitle = () => {
    if (activeTag) {
      return `Artigos sobre "${activeTag}" – Blog Gas2`;
    }
    return 'Blog – Gas2 | Dicas de Economia de Combustível';
  };

  const getPageDescription = () => {
    if (activeTag) {
      return `Artigos sobre ${activeTag} no blog do Gas2. Dicas de economia de combustível, manutenção veicular e gestão de abastecimentos.`;
    }
    return 'Artigos e dicas sobre economia de combustível, manutenção veicular e gestão de abastecimentos com o app Gas2.';
  };

  const canonicalPath = `/blog${activeTag ? `?tag=${encodeURIComponent(activeTag)}` : ''}`;

  return (
    <>
      <SEO
        title={getPageTitle()}
        description={getPageDescription()}
        canonicalPath={canonicalPath}
      />

      {/* Navbar sólida (branca) em página com fundo escuro */}
      <Navbar solid />

      <main className="min-h-screen bg-slate-950 pt-24 pb-20">
        {/* Cabeçalho da seção */}
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-16">
          <SectionTitle
            eyebrow="Blog Gas2"
            title={activeTag ? `Artigos sobre "${activeTag}"` : "Dicas e Novidades"}
            subtitle={activeTag
              ? `Artigos relacionados ao tema ${activeTag} no blog do Gas2.`
              : "Artigos sobre economia de combustível, manutenção preventiva e uso inteligente do Gas2."
            }
            align="center"
            dark
          />
        </section>

        {/* Nuvem de tags */}
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-8">
          <TagCloud
            allTags={allTags}
            activeTag={activeTag}
            onTagClick={handleTagClick}
          />
        </section>

        {/* Grid de cards ou estado vazio */}
        {paginatedPosts.length === 0 ? (
          <div className="text-center text-slate-400 py-20">
            <BookOpen className="mx-auto h-12 w-12 mb-4 opacity-40" strokeWidth={1.5} />
            <p className="text-lg font-medium">
              {activeTag ? `Nenhum artigo encontrado para "${activeTag}".` : 'Nenhum artigo publicado ainda.'}
            </p>
            <p className="text-sm mt-1">
              {activeTag ? 'Tente outro filtro ou volte para ver todos os artigos.' : 'Em breve novos conteúdos por aqui!'}
            </p>
            {activeTag && (
              <button
                onClick={() => handleTagClick(null)}
                className="mt-4 px-4 py-2 bg-amber-400 text-slate-950 rounded-lg font-medium hover:bg-amber-300 transition-colors"
              >
                Ver todos os artigos
              </button>
            )}
          </div>
        ) : (
          <>
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
              className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3"
            >
              {paginatedPosts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </motion.div>

            {/* Paginação */}
            <Pagination
              currentPage={safeCurrentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </main>

      <Footer />
    </>
  );
};

export default BlogList;

